import {useMemo} from 'react'
import {Card} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {GET_ONBOARDING_LIFE_CARRIERS} from '../../../gql/queries/agentOnboardingQueries'
import {SET_AGENT_ONBOARDING_CARRIER_APPOINTMENT} from '../../../gql/mutations/agentOnboardingMutations'
import {
  AppointmentStatus,
  ICarrierAppointment,
  ILifeCarrier,
} from '../../../types/agentOnboarding'

const M = defineMessages({
  title: {id: 'onboarding.carriers.title', defaultMessage: 'Carrier appointment status'},
  subtitle: {
    id: 'onboarding.carriers.subtitle',
    defaultMessage: 'Track contracting with each life carrier for this applicant.',
  },
  notStarted: {id: 'onboarding.carriers.NOT_STARTED', defaultMessage: 'Not started'},
  submitted: {id: 'onboarding.carriers.SUBMITTED', defaultMessage: 'Submitted'},
  reviewing: {id: 'onboarding.carriers.REVIEWING', defaultMessage: 'Reviewing'},
  contracted: {id: 'onboarding.carriers.CONTRACTED', defaultMessage: 'Contracted'},
  rejected: {id: 'onboarding.carriers.REJECTED', defaultMessage: 'Rejected'},
  saved: {id: 'onboarding.carriers.saved', defaultMessage: 'Carrier status updated'},
  failed: {id: 'onboarding.carriers.failed', defaultMessage: 'Could not update the carrier status'},
  empty: {id: 'onboarding.carriers.empty', defaultMessage: 'No life carriers configured.'},
})

const STATUSES: AppointmentStatus[] = [
  'NOT_STARTED',
  'SUBMITTED',
  'REVIEWING',
  'CONTRACTED',
  'REJECTED',
]

const TONE: Record<AppointmentStatus, string> = {
  // 'secondary' pinta gray-300 sobre gray-100 en este tema: ilegible. 'dark' mantiene el
  // gris neutro del fondo pero con texto oscuro.
  NOT_STARTED: 'dark',
  SUBMITTED: 'info',
  REVIEWING: 'warning',
  CONTRACTED: 'success',
  REJECTED: 'danger',
}

interface Props {
  applicationId: string
  appointments: ICarrierAppointment[]
  canEdit: boolean
  onSaved: () => void
}

export function CarrierAppointments({applicationId, appointments, canEdit, onSaved}: Props) {
  const intl = useIntl()
  const {data} = useQuery<{onboardingLifeCarriers: ILifeCarrier[]}>(GET_ONBOARDING_LIFE_CARRIERS)
  const [setAppointment, {loading: saving}] = useMutation(
    SET_AGENT_ONBOARDING_CARRIER_APPOINTMENT
  )

  const label = (status: AppointmentStatus) =>
    intl.formatMessage(
      {
        NOT_STARTED: M.notStarted,
        SUBMITTED: M.submitted,
        REVIEWING: M.reviewing,
        CONTRACTED: M.contracted,
        REJECTED: M.rejected,
      }[status]
    )

  // Absence of a row means "not started" — the backend never stores that state, so the panel
  // fills the gaps from the carrier catalogue.
  const byCarrier = useMemo(() => {
    const map = new Map<string, AppointmentStatus>()
    appointments.forEach((a) => map.set(String(a.carrierId), a.status))
    return map
  }, [appointments])

  const carriers = data?.onboardingLifeCarriers ?? []

  const handleChange = async (carrierId: string, status: AppointmentStatus) => {
    try {
      const res = await setAppointment({variables: {applicationId, carrierId, status}})
      const payload = res.data?.setAgentOnboardingCarrierAppointment
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.failed))
        return
      }
      toast.success(intl.formatMessage(M.saved))
      onSaved()
    } catch {
      toast.error(intl.formatMessage(M.failed))
    }
  }

  return (
    <Card>
      <Card.Header className='border-0 pt-6'>
        <div>
          <h3 className='fw-bold text-gray-900 mb-1'>{intl.formatMessage(M.title)}</h3>
          <span className='text-gray-600 fs-7'>{intl.formatMessage(M.subtitle)}</span>
        </div>
      </Card.Header>
      <Card.Body className='pt-2'>
        {carriers.length === 0 ? (
          <div className='text-gray-500'>{intl.formatMessage(M.empty)}</div>
        ) : (
          <div className='row g-4'>
            {carriers.map((carrier) => {
              const status = byCarrier.get(String(carrier.id)) ?? 'NOT_STARTED'
              return (
                <div className='col-xl-6' key={carrier.id}>
                  <div className='d-flex align-items-center justify-content-between gap-3 border border-gray-300 rounded p-4'>
                    <div className='d-flex align-items-center gap-3 min-w-0'>
                      <i className='bi bi-building fs-4 text-gray-500' />
                      <span className='fw-semibold text-gray-900 text-truncate'>{carrier.name}</span>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                      <span className={`badge badge-light-${TONE[status]}`}>{label(status)}</span>
                      {canEdit && (
                        <select
                          className='form-select form-select-sm w-auto'
                          value={status}
                          disabled={saving}
                          onChange={(e) =>
                            handleChange(String(carrier.id), e.target.value as AppointmentStatus)
                          }
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {label(s)}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
